import * as React from 'react';
import {
  isEmpty,
  isEqual,
} from 'lodash';
import {
  Divider,
  Modal,
} from '@revfluence/fresh';
import GuidelinesListEmpty
  from '@frontend/applications/TermsApp/components/ContentGuidelines/GuidelinesListEmpty/GuidelinesListEmpty';
import { TriangleExclamationIcon } from '@revfluence/fresh-icons/regular/esm';
import {
  TermsConfigsQuery_termsConfig_settings_contentGuidelines_attachments as Attachment,
} from '@frontend/app/queries/types/TermsConfigsQuery';
import { TContentGuideline } from '@frontend/applications/TermsApp/components/BulkTerms/types/ContentGuidelines';
import {
  ISortableGuideline,
  IContentGuidelineInstruction,
  IContentGuidelineTitle,
} from '@frontend/applications/TermsApp/components/BulkTerms/hooks/useState/actions';
import GuidelinesListItem
  // eslint-disable-next-line max-len
  from '@frontend/applications/TermsApp/components/ContentGuidelines/ContentGuidelinesList/GuidelinesListItems/GuidelinesListItem/GuidelinesListItem';
import { TemplateType } from '@frontend/applications/TermsApp/components/ContentGuidelines/types/state';
import { ContentType } from '@frontend/app/types/globalTypes';
import { P } from '@frontend/shadcn/components/typography/p';
import { CardContent } from '@frontend/shadcn/components/ui/card';
import { Card } from '@frontend/shadcn/components/ui/card';
import { Badge } from '@frontend/shadcn/components/ui/badge';
import { Button as ShadcnButton } from '@frontend/shadcn/components/ui/button';
import { PencilIcon, CloneIcon } from '@revfluence/fresh-icons/solid/esm';
import { FilterDrawer } from '@frontend/app/containers/ProductFulfillmentDashboard/FilterDrawer';
import { Input } from '@frontend/shadcn/components/ui/input';
import { CheckIcon, XmarkIcon } from '@revfluence/fresh-icons/regular/esm';
import { TrashIcon } from '@revfluence/fresh-icons/solid/esm';
import { Switch } from '@frontend/shadcn/components/ui/switch';
import { Label } from '@frontend/shadcn/components/ui/label';
import { useGetDefaultProgramTemplate, useGetTermsSettingsForProgram } from '@frontend/app/hooks';
import { useParams } from 'react-router-dom';
import PostAttachments from '../../../shared/PostAttachments/PostAttachments';
import PostInstructions from '../../../shared/PostInstructions/PostInstructions';

interface IProps {
  isReadOnly: boolean;
  shouldListSectionsOnly?: boolean;
  templateType: TemplateType;
  contentGuidelines: Array<TContentGuideline>;
  premadeTemplates: Array<TContentGuideline>;
  blankTemplates: Array<TContentGuideline>;
  otherProjectContentGuidelines: Array<TContentGuideline>;
  defaultActiveKey: number;
  isEditMode: boolean;
  onDuplicateContentGuideline: (contentGuideline: TContentGuideline) => void;
  onDeleteContentGuideline: (contentGuideline: TContentGuideline) => void;
  onSaveContentGuideline: (contentGuideline: TContentGuideline, isDefault?: boolean) => void;
  onUpdateContentGuidelineInstruction: (contentGuidelineInstruction: IContentGuidelineInstruction) => void
  onSortContentGuideline: (
    id: number,
    sortableGuideline: ISortableGuideline,
  ) => void;
  onAddContentGuidelineAttachment: (
    attachment: Attachment,
    id: number,
  ) => void;
  onDeleteContentGuidelineAttachment: (
    attachments: Array<Attachment>,
    id: number,
  ) => void;
  onUpdateContentGuidelineLabel: (id: number, label: string) => void;
  onUpdateContentGuidelineActiveKey: (defaultActiveKey: number) => void;
  onUpdateContentGuidelineTitle: (contentGuidelineTitle: IContentGuidelineTitle) => void;
  showFlexUI?: boolean;
}

const {
  useCallback,
  useMemo,
  createRef,
  useEffect,
  useState,
} = React;
const { confirm } = Modal;

const GuidelinesListItems: React.FC<IProps> = React.memo((props) => {
  const {
    isReadOnly,
    shouldListSectionsOnly = false,
    contentGuidelines,
    premadeTemplates,
    blankTemplates,
    templateType,
    otherProjectContentGuidelines,
    defaultActiveKey,
    isEditMode,
    onDuplicateContentGuideline,
    onDeleteContentGuideline,
    onSaveContentGuideline,
    onUpdateContentGuidelineInstruction,
    onSortContentGuideline,
    onAddContentGuidelineAttachment,
    onDeleteContentGuidelineAttachment,
    onUpdateContentGuidelineLabel,
    onUpdateContentGuidelineActiveKey,
    onUpdateContentGuidelineTitle,
    showFlexUI,
  } = props;

  const [isEditingDrawerOpen, setIsEditingDrawerOpen] = useState(false);
  const [selectedContentGuideline, setSelectedContentGuideline] = useState<TContentGuideline | null>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState('');
  const [tempIsDefault, setTempIsDefault] = useState(false);

  const { projectId } = useParams<{ projectId: string }>();

  useEffect(() => {
    if (selectedContentGuideline) {
      const newSelectedContentGuideline = contentGuidelines.find((contentGuideline) => contentGuideline.id === selectedContentGuideline.id);
      if (!isEqual(selectedContentGuideline, newSelectedContentGuideline)) {
        setSelectedContentGuideline(newSelectedContentGuideline ?? null);
      }
    }
  }, [selectedContentGuideline, contentGuidelines]);

  const {
    data: defaultTemplateData,
  } = useGetDefaultProgramTemplate({
    variables: {
      programId: parseInt(projectId ?? '0', 10),
    },
    skip: !showFlexUI || !projectId,
  });

  const {
    data: termsTemplateSettings,
  } = useGetTermsSettingsForProgram({
    variables: {
      programId: parseInt(projectId ?? '0', 10),
      termsType: defaultTemplateData?.defaultTemplate?.termsType,
    },
    skip: !projectId || !defaultTemplateData?.defaultTemplate?.termsType,
  });

  const defaultGuidelineId = useMemo(() => {
    return termsTemplateSettings?.termsTemplate?.contentGuidelineId ?? null;
  }, [termsTemplateSettings]);

  const guidelinesListItems = useMemo(() => {
    if (!isReadOnly) {
      return contentGuidelines;
    }
    if ([TemplateType.BlankTemplates, TemplateType.PremadeTemplates].includes(templateType)) {
      return templateType === TemplateType.BlankTemplates ? blankTemplates : premadeTemplates;
    }
    return otherProjectContentGuidelines || [];
  }, [
    isReadOnly,
    templateType,
    contentGuidelines,
    blankTemplates,
    premadeTemplates,
    otherProjectContentGuidelines,
  ]);

  const guidelinesListItemsRef = useMemo(
    () => guidelinesListItems.reduce(
      (acc, value) => {
        acc[value.id] = createRef<HTMLDivElement>();
        return acc;
      },
      {},
    ),
    [guidelinesListItems],
  );

  useEffect(() => {
    if (
      !isReadOnly
      && !isEditMode
      && defaultActiveKey !== 0
      && guidelinesListItemsRef[defaultActiveKey]?.current) {
      guidelinesListItemsRef[defaultActiveKey].current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [
    isReadOnly,
    isEditMode,
    defaultActiveKey,
    guidelinesListItemsRef,
  ]);

  const onConfirmDeleteGuideline = useCallback((contentGuideline: TContentGuideline) => {
    confirm({
      title: 'Are you sure you want to delete?',
      icon: <TriangleExclamationIcon color="#F5222D" />,
      content: `${contentGuideline.label} will be permanently deleted.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => onDeleteContentGuideline(contentGuideline),
      onCancel: () => {},
      autoFocusButton: null,
    });
  }, [onDeleteContentGuideline]);

  const handleCloseEditingDrawer = useCallback(() => {
    setIsEditingDrawerOpen(false);
    setSelectedContentGuideline(null);
  }, []);

  if (isEmpty(guidelinesListItems)) {
    return <GuidelinesListEmpty />;
  }

  if (showFlexUI) {
    return (
      <>
        <div className="flex flex-col gap-4">
          {guidelinesListItems.map((contentGuideline: TContentGuideline) => (
            <Card key={contentGuideline.id} className="rounded-2xl border border-border">
              <CardContent className="px-4 py-2 flex items-center justify-between">
                <P className="mb-0" fontWeight="font-medium">{contentGuideline.label}</P>
                <div className="flex items-center gap-2">
                  {contentGuideline.id === defaultGuidelineId && (
                  <Badge variant="secondary">Default</Badge>
                )}
                  <ShadcnButton
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setSelectedContentGuideline(contentGuideline);
                      setIsEditingDrawerOpen(true);
                      setTempTitle(contentGuideline.label);
                      setTempIsDefault(defaultGuidelineId === contentGuideline.id);
                    }}
                  >
                    <PencilIcon />
                  </ShadcnButton>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <FilterDrawer
          open={isEditingDrawerOpen}
          onOpenChange={setIsEditingDrawerOpen}
          headerProps={{
            title: 'Edit Content Guideline',
          }}
          footerButtons={[
            {
              name: 'Save',
              onClick: () => {
                onSaveContentGuideline(selectedContentGuideline, tempIsDefault);
                handleCloseEditingDrawer();
              },
              variant: 'primary' as const,
            },
            {
              name: 'Cancel',
              onClick: handleCloseEditingDrawer,
              variant: 'outline' as const,
            },
          ]}
          footerButtonAlignment="left"
          width="576px"
        >
          <div className="p-6 flex flex-col gap-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 flex-grow min-w-0">
                {isEditingTitle ? (
                  <div className="flex items-center gap-2 w-full">
                    <Input
                      value={tempTitle}
                      onChange={(e) => {
                        setTempTitle(e.target.value);
                      }}
                      className="text-base font-medium flex-grow"
                      placeholder="Enter Guideline Title"
                    />
                    <ShadcnButton
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 flex-shrink-0"
                      tooltip="Save Title"
                      onClick={() => {
                        onUpdateContentGuidelineLabel(
                          selectedContentGuideline?.id ?? 0,
                          tempTitle,
                        );
                        setIsEditingTitle(false);
                      }}
                    >
                      <CheckIcon />
                    </ShadcnButton>
                    <ShadcnButton
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 flex-shrink-0"
                      tooltip="Cancel Title Edit"
                      onClick={() => {
                        setTempTitle(selectedContentGuideline?.label ?? '');
                        setIsEditingTitle(false);
                      }}
                    >
                      <XmarkIcon />
                    </ShadcnButton>
                  </div>
            ) : (
              <div className="flex items-center gap-2 w-full min-w-0">
                <P className="text-base font-medium flex-grow border-0 p-0 h-auto focus-visible:ring-0 truncate" fontWeight="font-medium">{tempTitle}</P>
                <ShadcnButton variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" tooltip="Edit Title" onClick={() => setIsEditingTitle(true)}>
                  <PencilIcon />
                </ShadcnButton>
              </div>
            )}
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <>
                  <ShadcnButton
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    tooltip="Duplicate Guideline"
                    onClick={() => {
                    selectedContentGuideline.isCopy = true;
                      onDuplicateContentGuideline(selectedContentGuideline);
                      handleCloseEditingDrawer();
                    }}
                  >
                    <CloneIcon />
                  </ShadcnButton>
                  <ShadcnButton
                    variant="ghost"
                    size="icon"
                    className="text-destructive h-8 w-8"
                    tooltip="Delete Guideline"
                    onClick={() => {
                      onDeleteContentGuideline(selectedContentGuideline);
                      handleCloseEditingDrawer();
                    }}
                  >
                    <TrashIcon />
                  </ShadcnButton>
                </>
                <Switch
                  id="default-switch"
                  checked={tempIsDefault}
                  onCheckedChange={(checked) => {
                    setTempIsDefault(checked);
                  }}
                />
                <Label htmlFor="default-switch" className="text-sm font-normal whitespace-nowrap">Default</Label>
              </div>
            </div>

            { selectedContentGuideline && (
              <div className="flex flex-col gap-2">
                <P className="text-sm font-medium">Instructions</P>
                <PostInstructions
                  isReadOnly={isReadOnly}
                  id={selectedContentGuideline.id}
                  guidelines={selectedContentGuideline.guidelines}
                  onAddContentGuidelineInstruction={onUpdateContentGuidelineInstruction}
                  onUpdateContentGuidelineTitle={onUpdateContentGuidelineTitle}
                  onSortGuideline={onSortContentGuideline}
                />
              </div>
            )}

            { selectedContentGuideline && (
              <div className="flex flex-col gap-2">
                <PostAttachments
                  id={selectedContentGuideline.id}
                  attachments={selectedContentGuideline.attachments}
                  onAddAttachment={onAddContentGuidelineAttachment}
                  onRemoveAttachment={onDeleteContentGuidelineAttachment}
                />
              </div>
            )}
          </div>
        </FilterDrawer>
      </>
    );
  }

  return (
    <>
      {
        !isReadOnly
        && (
          <Divider style={{ margin: '0' }} />
        )
      }
      {
        guidelinesListItems.map((contentGuideline: TContentGuideline) => {
          if (ContentType.TWITTER_POST === contentGuideline.type) return null;
          return (
            <div
              key={contentGuideline.id}
              ref={guidelinesListItemsRef[contentGuideline.id]}
            >
              <GuidelinesListItem
                shouldListSectionsOnly={shouldListSectionsOnly}
                isReadOnly={isReadOnly}
                templateType={templateType}
                contentGuideline={contentGuideline}
                defaultActiveKey={defaultActiveKey}
                onDuplicateContentGuideline={onDuplicateContentGuideline}
                onDeleteContentGuideline={onConfirmDeleteGuideline}
                onSaveContentGuideline={onSaveContentGuideline}
                onUpdateContentGuidelineInstruction={onUpdateContentGuidelineInstruction}
                onSortContentGuideline={onSortContentGuideline}
                onAddContentGuidelineAttachment={onAddContentGuidelineAttachment}
                onDeleteContentGuidelineAttachment={onDeleteContentGuidelineAttachment}
                onUpdateContentGuidelineLabel={onUpdateContentGuidelineLabel}
                onUpdateContentGuidelineActiveKey={onUpdateContentGuidelineActiveKey}
                onUpdateContentGuidelineTitle={onUpdateContentGuidelineTitle}
              />
            </div>
          );
        })
      }
    </>
  );
});

GuidelinesListItems.displayName = 'GuidelinesListItems';

export default GuidelinesListItems;
